import React, { useState } from "react"
import { Helmet } from "react-helmet-async"
import { ChevronDown, ChevronUp, HelpCircle, Phone, Mail, Clock } from "lucide-react"

interface FAQItem {
  id: number
  question: string
  answer: string
  category: string
}

const FAQ: React.FC = () => {
  const [openItems, setOpenItems] = useState<number[]>([])
  const [activeCategory, setActiveCategory] = useState<string>("all")

  const toggleItem = (id: number) => {
    setOpenItems(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id)
        : [...prev, id]
    )
  }

  const faqData: FAQItem[] = [
    {
      id: 1,
      category: "ordering",
      question: "How can I place an order?",
      answer: "You can place an order through our website by adding items to your cart and proceeding to checkout. Alternatively, you can visit our showroom in Virar East or call us at +91 8010271912 to place an order over the phone."
    },
    {
      id: 2,
      category: "ordering",
      question: "What payment methods do you accept?",
      answer: "We accept cash, UPI, credit/debit cards, net banking, and EMI options. For large purchases, we also offer flexible payment plans. Contact us to discuss payment options that work best for you."
    },
    {
      id: 3,
      category: "ordering",
      question: "Can I cancel or modify my order?",
      answer: "Orders can be cancelled or modified within 24 hours of placement, provided the item hasn't entered production. For custom-made items, cancellation may not be possible once production has started. Please contact us immediately if you need to make changes."
    },
    {
      id: 4,
      category: "delivery",
      question: "How long does delivery take?",
      answer: "Ready-to-ship items are delivered within 2-5 business days. Custom-made furniture takes 15-30 business days. Delivery times may vary based on your location and item availability. We'll provide an estimated delivery date when you place your order."
    },
    {
      id: 5,
      category: "delivery",
      question: "Do you provide free delivery?",
      answer: "Yes! We offer free delivery on orders above ₹25,000 within our primary delivery zones including Virar East & West, Thane District, Mumbai Suburbs, and Palghar District. For orders below ₹25,000 or extended delivery zones, delivery charges apply."
    },
    {
      id: 6,
      category: "delivery",
      question: "Do you provide assembly service?",
      answer: "Yes, we offer professional assembly and installation services for an additional fee ranging from ₹300 to ₹1,500 depending on the furniture type. Our skilled technicians ensure proper assembly and placement of your furniture."
    },
    {
      id: 7,
      category: "products",
      question: "Do you offer custom furniture?",
      answer: "Yes, we specialize in custom-made furniture tailored to your specific requirements, space, and design preferences. Our team can create bespoke pieces including modular kitchens, wardrobes, and custom seating. Contact us to discuss your custom furniture needs."
    },
    {
      id: 8,
      category: "products",
      question: "What materials do you use?",
      answer: "We use high-quality materials including solid wood, engineered wood, premium fabrics, leather, and metal hardware. All materials are sourced from trusted suppliers and undergo quality checks to ensure durability and longevity."
    },
    {
      id: 9,
      category: "products",
      question: "Do you provide warranty on furniture?",
      answer: "Yes, we provide warranty coverage up to 3 years on most furniture items, covering manufacturing defects and structural issues. Warranty terms vary by product type. Custom furniture and certain items may have different warranty periods."
    },
    {
      id: 10,
      category: "returns",
      question: "What is your return policy?",
      answer: "We offer a 30-day return policy on eligible standard furniture items. Items must be in original condition with tags and packaging intact. Custom-made items, mattresses, and personalized furniture cannot be returned. Return charges may apply for customer change of mind."
    },
    {
      id: 11,
      category: "returns",
      question: "How do I return an item?",
      answer: "To return an item, call us at +91 9876 within 30 days of delivery. We'll provide a Return Authorization Number and schedule a pickup. Our team will inspect the item and process your refund within 7-10 business days if approved."
    },
    {
      id: 12,
      category: "general",
      question: "Where is your showroom located?",
      answer: "Our showroom is located in Virar East, Thane, Maharashtra 401305. We're open Monday to Saturday from 9:00 AM to 8:00 PM, and Sunday from 10:00 AM to 6:00 PM. You can visit us to see our furniture collection in person."
    },
    {
      id: 13,
      category: "general",
      question: "Do you offer interior design consultation?",
      answer: "Yes, our experienced team provides interior design consultation to help you choose the right furniture for your space. We can suggest layouts, color combinations, and furniture pieces that complement your home's style and your personal preferences."
    },
    {
      id: 14,
      category: "general",
      question: "How can I track my order?",
      answer: "Once your order is confirmed, we'll provide updates via SMS and phone calls. For custom orders, we'll keep you informed about the production status. You can also call us anytime to check on your order status."
    }
  ]

  const categories = [
    { id: "all", name: "All Questions" },
    { id: "ordering", name: "Ordering" },
    { id: "delivery", name: "Delivery" },
    { id: "products", name: "Products" },
    { id: "returns", name: "Returns" },
    { id: "general", name: "General" }
  ]

  const filteredFAQs = activeCategory === "all" 
    ? faqData 
    : faqData.filter(item => item.category === activeCategory)

  return (
    <>
      <Helmet>
        <title>Frequently Asked Questions - Anmol Furniture Bazar</title>
        <meta 
          name="description" 
          content="Find answers to common questions about Anmol Furniture Bazar. Learn about our ordering, delivery, returns, and furniture products." 
        />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-12">
            <HelpCircle className="w-16 h-16 text-primary-600 mx-auto mb-4" />
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Find quick answers to the most common questions about our furniture, services, and policies
            </p>
          </div>

          {/* Category Filter */}
          <div className="mb-8">
            <div className="flex flex-wrap justify-center gap-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    activeCategory === category.id
                      ? "bg-primary-600 text-white shadow-lg"
                      : "bg-white text-gray-700 hover:bg-primary-50 border border-gray-300"
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          {/* FAQ List */}
          <div className="max-w-4xl mx-auto">
            <div className="space-y-4">
              {filteredFAQs.map((item) => (
                <div key={item.id} className="bg-white rounded-xl shadow-lg overflow-hidden">
                  <button
                    onClick={() => toggleItem(item.id)}
                    className="w-full px-6 py-4 text-left hover:bg-gray-50 transition-colors duration-200 focus:outline-none"
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 pr-4">
                        {item.question}
                      </h3>
                      {openItems.includes(item.id) ? (
                        <ChevronUp className="w-5 h-5 text-primary-600 flex-shrink-0" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                      )}
                    </div>
                  </button>
                  {openItems.includes(item.id) && (
                    <div className="px-6 pb-4">
                      <div className="border-t border-gray-200 pt-4">
                        <p className="text-gray-700 leading-relaxed">
                          {item.answer}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Contact Section */}
          <div className="mt-16 bg-white rounded-xl shadow-lg p-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-semibold text-gray-900 mb-4">Still Have Questions?</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Can't find the answer you're looking for? Our friendly customer support team is here to help you with any questions or concerns.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center p-6 bg-blue-50 rounded-xl">
                <Phone className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Call Us</h3>
                <p className="text-gray-600 mb-3">Speak directly with our team</p>
                <div className="space-y-1">
                  <p className="font-medium text-blue-600">+91 8010271912</p>
                  <p className="font-medium text-blue-600">+91 9987023567</p>
                </div>
              </div>

              <div className="text-center p-6 bg-green-50 rounded-xl">
                <Mail className="w-12 h-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Email Us</h3>
                <p className="text-gray-600 mb-3">Send us your questions</p>
                <div className="space-y-1">
                  <p className="font-medium text-green-600">info@anmolfurniture.com</p>
                  <p className="font-medium text-green-600">support@anmolfurniture.com</p>
                </div>
              </div>

              <div className="text-center p-6 bg-purple-50 rounded-xl">
                <Clock className="w-12 h-12 text-purple-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Visit Showroom</h3>
                <p className="text-gray-600 mb-3">See furniture in person</p>
                <div className="space-y-1 text-sm">
                  <p className="font-medium text-purple-600">Mon-Sat: 9AM-8PM</p>
                  <p className="font-medium text-purple-600">Sunday: 10AM-6PM</p>
                  <p className="text-gray-600 mt-2">Virar East, Thane</p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Tips */}
          <div className="mt-12 bg-gradient-to-r from-primary-500 to-primary-600 text-white p-6 rounded-xl">
            <h3 className="text-xl font-semibold mb-4 text-center">💡 Quick Tips for Better Service</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="flex items-start space-x-2">
                <div className="w-1.5 h-1.5 bg-white rounded-full mt-2 flex-shrink-0"></div>
                <span>Keep your order number handy when calling for order updates</span>
              </div>
              <div className="flex items-start space-x-2">
                <div className="w-1.5 h-1.5 bg-white rounded-full mt-2 flex-shrink-0"></div>
                <span>Measure your space before ordering to ensure perfect fit</span>
              </div>
              <div className="flex items-start space-x-2">
                <div className="w-1.5 h-1.5 bg-white rounded-full mt-2 flex-shrink-0"></div>
                <span>Ask about assembly service when placing your order</span>
              </div>
              <div className="flex items-start space-x-2">
                <div className="w-1.5 h-1.5 bg-white rounded-full mt-2 flex-shrink-0"></div>
                <span>Check our Instagram @anmol_furniture_bazar for latest designs</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default FAQ